import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button, Formblock, FormSection, Message, Select, Textfield,
} from '@zolteam/zol-admin-ui';
import { Form, FormElements } from '@zolteam/zol-react-form';
// Locales
import fr from '../../locales/fr.json';
// hooks
import { useGetAdminListQuery } from '../../services/users/usersQueries';
import { useFlightBrief } from '../../domain/flightBrief/useFlightBrief';
import { createFlightBrief, updateFlightBrief } from '../../services/flightBriefs/flightBriefsService';
import { useGetFlightBrief } from '../../services/flightBriefs/flightBriefsQueries';
import { useGetBookingQuery } from '../../services/bookings/bookingsQueries';
import { useGetAirport } from '../../services/airports/airportsQueries';
import { useGetTerminal } from '../../services/airports/terminals/adminTerminalQueries';
// utils

const BRIEF_FORM = 'flightBriefForm';

/**
 * FlightBriefNew component.
 * @returns {JSX.Element}
 */
function FlightBriefNew() {
  // states
  const [dutyOfficer, setDutyOfficer] = useState(null);
  // Hooks
  const { uuid, id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: admins } = useGetAdminListQuery({
    pageNumber: 1,
    pageSize: 100,
  });
  const {
    data: flightBrief,
  } = useGetFlightBrief(
    uuid,
    { enabled: !!uuid },
  );
  // Based on flightBrief.bookingId, fetch the booking?.departure?.airportId and booking?.arrival?.airportId.
  // After that, fetch the airports data to get the airports terminals.
  const {
    data: booking,
  } = useGetBookingQuery(
    id,
    { enabled: !!id },
  );
  const departureAirportId = booking?.departure?.airportId;
  const arrivalAirportId = booking?.arrival?.airportId;

  // Note: we fetch airport details only when needed via queries elsewhere; keep airport ids from booking

  const {
    FLIGHT_BRIEF_SCHEMA,
    flightBriefDefaultValues,
    splitBriefForm,
  } = useFlightBrief({
    users: admins?.data,
    flightBrief,
  });

  const [selectedDepartureTerminal, setSelectedDepartureTerminal] = useState(null);
  const [selectedArrivalTerminal, setSelectedArrivalTerminal] = useState(null);

  const { data: departureAirport } = useGetAirport(departureAirportId, { options: { enabled: !!departureAirportId } });
  const { data: arrivalAirport } = useGetAirport(arrivalAirportId, { options: { enabled: !!arrivalAirportId } });

  const { data: departureTerminalDetails } = useGetTerminal(departureAirportId, selectedDepartureTerminal, { options: { enabled: !!departureAirportId && !!selectedDepartureTerminal } });
  const { data: arrivalTerminalDetails } = useGetTerminal(arrivalAirportId, selectedArrivalTerminal, { options: { enabled: !!arrivalAirportId && !!selectedArrivalTerminal } });

  const [departureMeetingInfo, setDepartureMeetingInfo] = useState('');
  const [arrivalMeetingInfo, setArrivalMeetingInfo] = useState('');

  useEffect(() => {
    setDepartureMeetingInfo(flightBriefDefaultValues?.departureTerminalMeetingInfo || '');
    setArrivalMeetingInfo(flightBriefDefaultValues?.arrivalTerminalMeetingInfo || '');
  }, [flightBriefDefaultValues]);

  useEffect(() => {
    setDutyOfficer(flightBrief?.dutyOfficer?.id || null);
  }, [flightBrief?.dutyOfficer]);

  useEffect(() => {
    setSelectedArrivalTerminal(flightBrief?.arrivalTerminalId ? String(flightBrief?.arrivalTerminalId) : null);
    setSelectedDepartureTerminal(flightBrief?.departureTerminalId ? String(flightBrief?.departureTerminalId) : null);
  }, [flightBrief?.arrivalTerminalId, flightBrief?.departureTerminalId]);

  // API Calls
  const postCreateFlightBriefMutation = useMutation(createFlightBrief, {
    onSuccess: () => {
      navigate(`/bookings/${id}`);
    },
  });
  const putUpdateFlightBriefMutation = useMutation(updateFlightBrief, {
    onSuccess: () => {
      navigate(`/bookings/${id}`);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error updating flight brief:', error);
    },
  });

  const adminOptions = useMemo(() => (admins?.data?.map(
    /**
     * @param {User} admin
     * @returns {{label: string, value: number}}
     */
    ({ firstname, lastname, id: adminId }) => ({ label: `${firstname} ${lastname}`, value: adminId }),
  ) || []), [admins]);

  // Handlers
  /**
   * Set the duty officer.
   * @param {{value: number, label: string}} newDutyOfficer - The selected duty officer.
   * @param {any} formInfo
   */
  const handleSetDutyOfficer = (newDutyOfficer, formInfo) => {
    setDutyOfficer(newDutyOfficer);
    // @ts-ignore - using dynamic field name from form config
    formInfo.formMethods.setValue('dutyOfficerId', newDutyOfficer, { shouldValidate: true });
  };
  /**
   * @param {any} formValues
   * @returns {void}
   */
  const onSubmit = (formValues) => {
    const mappedFormValues = {
      bookingId: parseInt(window.location.pathname.split('/')[2], 10),
      flightDuration: formValues.flightDuration ? formValues.flightDuration?.toString() : '',
      flightNumber: formValues.flightNumber || '',
      aircraft: {
        type: formValues.planeType || '',
        registration: formValues.planeRegistration || '',
        seatNumber: formValues.planeSeats ? parseInt(formValues.planeSeats, 10) : null,
      },
      // Use local states as source of truth for terminal IDs and meeting info
      // TODO : This needs to be cleaned and refactored to unify the form values and the local states.
      departureTerminalId: selectedDepartureTerminal ? parseInt(selectedDepartureTerminal, 10) : null,
      departureMeetingInfo: departureMeetingInfo || '',
      arrivalTerminalId: selectedArrivalTerminal ? parseInt(selectedArrivalTerminal, 10) : null,
      arrivalMeetingInfo: arrivalMeetingInfo || '',
      information: formValues.information,
      flightRadarLink: formValues.flightRadarLink,
      dutyOfficerId: dutyOfficer ? parseInt(dutyOfficer, 10) : null,
      passengerIdList: flightBriefDefaultValues.passengerIdList || [],
      displayCompanyLogo: !!formValues.displayCompanyLogo,
    };

    if (uuid) {
      // @ts-ignore - payload intentionally coerced to match API (int|null)
      return putUpdateFlightBriefMutation.mutate(/** @type {any} */({ ...mappedFormValues, uuid }));
    }
    // @ts-ignore - payload intentionally coerced to match API (int|null)
    return postCreateFlightBriefMutation.mutate(/** @type {any} */mappedFormValues);
  };

  return (
    <div className="padding-l relative">
      <div
        id="form-header"
        className="d-flex f-row gap-m f-wrap justify-between padding-top-l sticky-top
        background-color-neutral-00 foreground-index"
      >
        <h1 className="h1-default" style={{ fontWeight: 'bold' }}>
          {flightBrief ? t('flightBriefForm.title.update') : t('flightBriefForm.title.new')}
        </h1>
        <div className="d-flex f-row gap-s f-wrap">
          <Button color="neutral00" className="underline">
            <a href="#flight">{t('flightBriefForm.sections.flight')}</a>
          </Button>
          <Button color="neutral00" className="underline">
            <a href="#plane">{t('flightBriefForm.sections.plane')}</a>
          </Button>
          <Button color="neutral00" className="underline">
            <a href="#locationdep">{t('flightBriefForm.sections.departureLocation')}</a>
          </Button>
          <Button color="neutral00" className="underline">
            <a href="#locationarr">{t('flightBriefForm.sections.arrivalLocation')}</a>
          </Button>
          <Button color="neutral00" className="underline">
            <a href="#info">{t('flightBriefForm.sections.info')}</a>
          </Button>
          <Button color="primary">
            <Link to={`/bookings/${id}`}>{t('flightBriefForm.actions.bookingDetails')}</Link>
          </Button>
        </div>
      </div>
      {postCreateFlightBriefMutation.isError ? (
        <div className="margin-bottom-m">
          <Message>
            {t(`apiErrors.${postCreateFlightBriefMutation.error
            // @ts-ignore
              ?.code}`)}
          </Message>
        </div>
      ) : null}

      {(uuid && flightBrief) || !uuid ? (
        <Form
          key={flightBriefDefaultValues ? JSON.stringify(flightBriefDefaultValues) : 'newFlightBriefForm'}
          name={BRIEF_FORM}
          callbacks={{ onSubmit }}
          schema={FLIGHT_BRIEF_SCHEMA}
          defaultValues={flightBriefDefaultValues}
          translations={{ formValidation: fr.formValidation }}
        >
          {(formConfig, formInfo) => {
            // Debug: inspect runtime formConfig shape to ensure keys match splitBriefForm expectations
            // eslint-disable-next-line no-console
            console.log('FlightBrief formConfig keys:', Object.keys(formConfig));
            // eslint-disable-next-line no-console
            console.log('Sample formConfig entry:', Object.entries(formConfig)[0]);
            const formConfigParts = splitBriefForm(formConfig);
            return (
              <>
                <div id="flight">
                  <FormSection label={t('flightBriefForm.sections.flight')} className="margin-top-m">
                    <Formblock
                      key="dutyOfficer"
                      htmlFor="dutyOfficer"
                      className="d-flex margin-bottom-xs"
                    >
                      <label htmlFor="dutyofficer" className="p2-default">
                        <span>{t('flightBriefForm.fields.dutyOfficer')}</span>
                        {adminOptions ? (
                          <Select
                            // @ts-ignore
                            id="dutyOfficer"
                            name="dutyOfficer"
                            className="background-neutral00"
                            placeholder={t('flightBriefForm.fields.dutyOfficer')}
                            options={adminOptions}
                            value={dutyOfficer || ''}
                            onChange={
                              (
                                /** @type {{target:{value:{ value: number; label: string; }; }; }} */ e,
                              ) => handleSetDutyOfficer(e?.target?.value, formInfo)
                            }
                          />
                        ) : null}
                      </label>
                    </Formblock>
                    <FormElements
                      translations={null}
                      formConfig={formConfigParts.flightPart}
                      formTitle={formInfo.formName}
                    />
                  </FormSection>
                </div>
                <div id="plane">
                  <FormSection label={t('flightBriefForm.sections.plane')} className="margin-top-m">
                    <FormElements
                      translations={null}
                      formConfig={formConfigParts.planePart}
                      formTitle={formInfo.formName}
                    />
                  </FormSection>
                </div>
                <div id="locationdep">
                  <FormSection
                    label={t('flightBriefForm.sections.departureLocation')}
                    className="margin-top-m"
                  >
                    <div className="d-flex justify-end">
                      <Button icon="add" onClick={() => navigate(`/airports/${departureAirportId}/terminals/new?flightBriefId=${uuid || ''}&bookingId=${id || ''}`)}>
                        {t('terminalForm.actions.add')}
                      </Button>
                    </div>
                    <div className="d-flex f-row gap-s align-end">
                      {
                        (() => {
                          const base = departureAirport?.terminals || departureAirport?.data?.terminals || [];
                          const opts = base.map(
                            /**
                             * @param {{ id: number | string; name: string }} x
                             * @returns {{value: string, label: string}}
                             */
                            (x) => ({ value: String(x.id), label: x.name }),
                          );
                          if (selectedDepartureTerminal && !opts.find(
                            /**
                             * @param {{ value: string; label: string }} o
                             * @returns {boolean}
                             */
                            (o) => o.value === selectedDepartureTerminal,
                          )) {
                            opts.unshift({ value: selectedDepartureTerminal, label: departureTerminalDetails?.name || '' });
                          }
                          return (
                            <Select
                            // @ts-ignore
                              id="departureTerminal"
                              name="departureTerminalId"
                              className="background-neutral00 margin-top-s"
                              placeholder={t('flightBriefForm.fields.departureTerminalName')}
                              options={opts}
                              value={selectedDepartureTerminal || ''}
                              onChange={(/** @type {{ target: { value: any; }; }} */ e) => {
                                const value = e?.target?.value || '';
                                setSelectedDepartureTerminal(value || null);
                                // @ts-ignore - using dynamic field name from form config
                                formInfo.formMethods.setValue('departureTerminalId', value || '', { shouldValidate: true });
                              }}
                            />
                          );
                        })()
                      }
                    </div>
                    <div className="d-flex f-row gap-xs margin-top-s">
                      {selectedDepartureTerminal ? (
                        <Button onClick={() => navigate(`/airports/${departureAirportId}/terminals/${selectedDepartureTerminal}?flightBriefId=${uuid || ''}&bookingId=${id || ''}`)}>
                          {t('terminalForm.actions.update')}
                        </Button>
                      ) : null}
                    </div>
                    <Formblock htmlFor="departureMeetingInfo" label={t('flightBriefForm.fields.departureTerminalMeetingInfo')} className="margin-top-m" key="departureMeetingInfo">
                      <Textfield
                        // @ts-ignore
                        id="departureMeetingInfo"
                        name="departureMeetingInfo"
                        value={departureMeetingInfo}
                        onChange={(/** @type {{ target: { value: any; }; }} */ e) => setDepartureMeetingInfo(e.target.value)}
                      />
                    </Formblock>
                  </FormSection>
                </div>
                <div id="locationarr">
                  <FormSection
                    label={t('flightBriefForm.sections.arrivalLocation')}
                    className="margin-top-xs"
                  >
                    <div className="d-flex justify-end">
                      <Button icon="add" onClick={() => navigate(`/airports/${arrivalAirportId}/terminals/new?flightBriefId=${uuid || ''}&bookingId=${id || ''}`)}>
                        {t('terminalForm.actions.add')}
                      </Button>
                    </div>
                    <div className="d-flex f-row gap-s align-end">
                      {
                        (() => {
                          const base = arrivalAirport?.terminals || arrivalAirport?.data?.terminals || [];
                          const opts = base.map(
                            /**
                             * @param {{ id: number | string; name: string }} y
                             * @returns {{value: string, label: string}}
                             */
                            (y) => ({ value: String(y.id), label: y.name }),
                          );
                          if (selectedArrivalTerminal && !opts.find(
                            /**
                             * @param {{ value: string; label: string }} o
                             * @returns {boolean}
                             */ (o) => o.value === selectedArrivalTerminal,
                          )) {
                            opts.unshift({ value: selectedArrivalTerminal, label: arrivalTerminalDetails?.name || '' });
                          }
                          return (
                            <Select
                              // @ts-ignore
                              id="arrivalTerminal"
                              name="arrivalTerminal"
                              className="background-neutral00 margin-top-s"
                              placeholder={t('flightBriefForm.fields.arrivalTerminalName')}
                              options={opts}
                              value={selectedArrivalTerminal || ''}
                              onChange={(/** @type {{ target: { value: any; }; }} */ e) => {
                                const value = e?.target?.value || '';
                                setSelectedArrivalTerminal(value || null);
                                // @ts-ignore - using dynamic field name from form config
                                formInfo.formMethods.setValue('arrivalTerminalId', value || '', { shouldValidate: true });
                              }}
                            />
                          );
                        })()
                      }
                    </div>
                    <div className="d-flex f-row gap-xs margin-top-s">
                      {selectedArrivalTerminal ? (
                        <Button onClick={() => navigate(`/airports/${arrivalAirportId}/terminals/${selectedArrivalTerminal}?flightBriefId=${uuid || ''}&bookingId=${id || ''}`)}>
                          {t('terminalForm.actions.update')}
                        </Button>
                      ) : null}
                    </div>
                    <Formblock htmlFor="arrivalMeetingInfo" label={t('flightBriefForm.fields.arrivalTerminalMeetingInfo')} className="margin-top-m" key="arrivalMeetingInfo">
                      <Textfield
                        // @ts-ignore
                        id="arrivalMeetingInfo"
                        name="arrivalMeetingInfo"
                        value={arrivalMeetingInfo}
                        onChange={(/** @type {{ target: { value: any; }; }} */ e) => setArrivalMeetingInfo(e.target.value)}
                      />
                    </Formblock>
                  </FormSection>
                </div>
                <div id="info">
                  <FormSection label={t('flightBriefForm.sections.info')} className="margin-top-m">
                    <FormElements
                      translations={{ selectOptionLabels: {}, mediaGallery: null }}
                      formConfig={formConfigParts.infoPart}
                      formTitle={formInfo.formName}
                    />
                  </FormSection>
                </div>
                <Button
                  className="margin-top-m"
                  form={BRIEF_FORM}
                  isSubmit
                  isLoading={postCreateFlightBriefMutation.isLoading}
                >
                  {uuid ? t('flightBriefForm.actions.update') : t('flightBriefForm.actions.submit')}
                </Button>
              </>
            );
          }}
        </Form>
      ) : null}
    </div>
  );
}

export default FlightBriefNew;
